import Phaser from 'phaser';
import { Economy } from './Economy';
import { CrowdManager } from './CrowdManager';
import { IsoGrid } from '@/iso/IsoGrid';
import { STARTING_BUDGET, SIMULATION_TICK_MS, EquipmentType, EQUIPMENT_DEFS } from '@/config';
import { EventBus, GameEvent } from '@/utils/EventBus';

interface EquipmentInfo {
  type: EquipmentType;
  col: number;
  row: number;
}

/**
 * Orchestrates the simulation: economy, crowd, coverage.
 * Runs on a fixed timestep (4 ticks/sec).
 */
export class SimulationManager {
  readonly economy: Economy;
  readonly crowdManager: CrowdManager;
  private accumulator = 0;
  private equipment: EquipmentInfo[] = [];
  private coverageDirty = true;

  constructor(scene: Phaser.Scene, grid: IsoGrid) {
    this.economy = new Economy(STARTING_BUDGET);
    this.crowdManager = new CrowdManager(scene, grid);

    EventBus.on(GameEvent.EQUIPMENT_PLACED, (data: { type: EquipmentType; col: number; row: number; cost: number }) => {
      this.equipment.push({ type: data.type, col: data.col, row: data.row });
      this.economy.spend(data.cost);
      this.coverageDirty = true;
    });

    EventBus.on(GameEvent.EQUIPMENT_REMOVED, (data: { type: EquipmentType; col: number; row: number; refund: number }) => {
      this.equipment = this.equipment.filter(
        (e) => !(e.col === data.col && e.row === data.row)
      );
      this.economy.addIncome(data.refund);
      this.coverageDirty = true;
    });

    EventBus.on(GameEvent.EQUIPMENT_MOVED, (data: { type: EquipmentType; fromCol: number; fromRow: number; toCol: number; toRow: number }) => {
      const eq = this.equipment.find((e) => e.type === data.type && e.col === data.fromCol && e.row === data.fromRow);
      if (eq) {
        eq.col = data.toCol;
        eq.row = data.toRow;
        this.coverageDirty = true;
      }
    });
  }

  /** Call from scene update with delta in ms */
  update(deltaMs: number): void {
    this.accumulator += deltaMs;

    while (this.accumulator >= SIMULATION_TICK_MS) {
      this.accumulator -= SIMULATION_TICK_MS;
      this.tick();
    }
  }

  private tick(): void {
    // Refresh coverage if equipment changed
    if (this.coverageDirty) {
      this.crowdManager.refreshCoverage(this.equipment);
      this.coverageDirty = false;
    }

    // Operating costs
    let totalOpCost = 0;
    for (const eq of this.equipment) {
      totalOpCost += EQUIPMENT_DEFS[eq.type].operatingCost;
    }
    this.economy.deductOperatingCosts(totalOpCost);

    // Tick crowd
    this.crowdManager.tick(this.economy);

    // Emit budget update
    EventBus.emit(GameEvent.BUDGET_CHANGED, this.economy.balance);
  }

  destroy(): void {
    this.crowdManager.destroy();
  }
}
